import React, { useState, useRef, useEffect } from 'react';
import { FiSliders, FiChevronDown } from 'react-icons/fi';
import { AnimatePresence } from 'framer-motion';
import { ServiceFilterModal } from './ServiceFilterModal';
import { InquirySourceModal } from './InquirySourceModal';
import { DateRangePicker } from '../common/DateRangePicker';
import type { DateRange } from '../common/DateRangePicker';

export type InquiryStatusFilter = 'All' | 'new' | 'replied' | 'contracted' | 'closed';

const statusOptions: { id: InquiryStatusFilter; label: string }[] = [
  { id: 'All', label: 'All' },
  { id: 'new', label: 'New' },
  { id: 'replied', label: 'Replied' },
  { id: 'contracted', label: 'Offer Sent' },
  { id: 'closed', label: 'Closed' },
];

interface InquiriesFiltersProps {
  statusFilter: InquiryStatusFilter;
  onStatusFilterChange: (status: InquiryStatusFilter) => void;
  dateRange: DateRange;
  onDateRangeChange: (range: DateRange) => void;
  selectedService: string;
  onServiceChange: (service: string) => void;
  selectedSource?: string;
  onSourceChange?: (source: string) => void;
  servicesList: string[];
  sourcesList?: string[];
  onAddSource?: (newSource: string) => void;
  onDeleteSource?: (sourceToDelete: string) => void;
}

export const InquiriesFilters: React.FC<InquiriesFiltersProps> = ({
  statusFilter,
  onStatusFilterChange,
  dateRange,
  onDateRangeChange,
  selectedService,
  onServiceChange,
  selectedSource,
  onSourceChange,
  servicesList,
  sourcesList,
  onAddSource,
  onDeleteSource,
}) => {
  const [showServicePicker, setShowServicePicker] = useState(false);
  const [showSourcePicker, setShowSourcePicker] = useState(false);

  const serviceRef = useRef<HTMLDivElement>(null);
  const sourceRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (serviceRef.current && !serviceRef.current.contains(e.target as Node)) {
        setShowServicePicker(false);
      }
      if (sourceRef.current && !sourceRef.current.contains(e.target as Node)) {
        setShowSourcePicker(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const filterButtonClasses =
    'inline-flex items-center gap-2 rounded-full border border-[#3A3A3C] bg-[#2C2C2E] px-4 py-2 text-sm font-medium text-zinc-200 transition hover:border-[#48484A] hover:bg-[#3A3A3C] cursor-pointer';

  return (
    <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      {/* Left status filter pills inside container */}
      <div className="inline-flex max-w-full items-center overflow-x-auto rounded-full border border-[#2C2C2E] bg-[#1C1C1E] p-1 no-scrollbar shrink-0">
        {statusOptions.map((opt) => {
          const isActive = statusFilter === opt.id;
          return (
            <button
              key={opt.id}
              onClick={() => onStatusFilterChange(opt.id)}
              className={`rounded-full px-4 sm:px-5 py-1.5 text-xs sm:text-sm font-medium transition whitespace-nowrap ${
                isActive ? 'bg-zinc-800 text-white shadow-sm' : 'text-zinc-400 hover:text-zinc-200'
              }`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>

      {/* Right dropdown filters */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Date Filter Dropdown with Range Picker */}
        <DateRangePicker value={dateRange} onChange={onDateRangeChange} />

        {/* Service Filter Dropdown */}
        <div ref={serviceRef} className="relative">
          <button
            onClick={() => {
              setShowServicePicker((prev) => !prev);
              setShowSourcePicker(false);
            }}
            className={filterButtonClasses}
          >
            <FiSliders size={16} className="text-[#3B82F6] shrink-0" />
            <span className="truncate max-w-[120px] sm:max-w-[200px]">
              {selectedService === 'All' ? 'Service' : selectedService}
            </span>
            <FiChevronDown size={14} className="text-zinc-400 shrink-0" />
          </button>

          <AnimatePresence>
            {showServicePicker && (
              <ServiceFilterModal
                services={servicesList}
                selectedService={selectedService}
                onSelectService={onServiceChange}
                onClose={() => setShowServicePicker(false)}
              />
            )}
          </AnimatePresence>
        </div>

        {/* Inquiry Sources Filter Dropdown (Optional) */}
        {sourcesList && sourcesList.length > 0 && onSourceChange && selectedSource && (
          <div ref={sourceRef} className="relative">
            <button
              onClick={() => {
                setShowSourcePicker((prev) => !prev);
                setShowServicePicker(false);
              }}
              className={filterButtonClasses}
            >
              <span>{selectedSource === 'All' ? 'Inquiry Sources' : selectedSource}</span>
              <FiChevronDown size={14} className="text-zinc-400" />
            </button>

            <AnimatePresence>
              {showSourcePicker && (
                <InquirySourceModal
                  sources={sourcesList}
                  selectedSource={selectedSource}
                  onSelectSource={onSourceChange}
                  onAddSource={onAddSource}
                  onDeleteSource={onDeleteSource}
                  onClose={() => setShowSourcePicker(false)}
                />
              )}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
};
